export const ROUTE_PATHS = {
  HOME: '/',
  AUDIT: '/audit',
  RESULTS_MASTER: '/results',
  ACTION_RECOMMENDATIONS: '/handlungsempfehlungen',
  PAYWALL: '/handlungsempfehlung-freischalten',
  SALES_ENGINE: '/sales-engine',
  KONTAKT: '/kontakt',
  UEBER_UNS: '/ueber-uns',
  ADMIN_LOGIN: '/admin-login',
  ADMIN: '/admin',
  IMPRESSUM: '/impressum',
  DATENSCHUTZ: '/datenschutz',
  AGB: '/agb',
} as const;

export interface CompanyProfile {
  companyName: string;
  industry: string;
  revenue: string;
  employees: string;
  teamSize: string;
  businessModel: 'B2B' | 'B2C' | 'Both';
  averageDealValue: string;  // Dropdown-Wert (z.B. "1.000€ - 5.000€")
  averageDealValueExact?: number;  // Exakter Wert in € (optional)
  salesCycle: string;  // Dropdown-Wert (z.B. "1-3 Monate")
  salesCycleExact?: number;  // Exakter Wert in Monaten (optional)
  hasCRM: boolean;
  crmName?: string;
  hasMarketingAutomation: boolean;
  marketingAutomationName?: string;
  leadSources: string[];
  contactFirstName: string;
  contactLastName: string;
  contactPosition: string;
  contactPhone?: string;
  contactEmail: string;
  
  // Sales-Kennzahlen (für präzise Umsatzverlust-Berechnung)
  currentMonthlyRevenue?: number; // Aktueller monatlicher Umsatz in €
  currentMonthlyRevenueUnknown?: boolean; // "Nicht bekannt" Flag
  
  averageDealValueUnknown?: boolean; // "Nicht bekannt" Flag für Deal-Größe
  salesCycleUnknown?: boolean; // "Nicht bekannt" Flag für Sales Cycle
  
  // Akquise-Methoden (Prozentuale Verteilung)
  acquisitionMethods?: {
    phone?: number;           // Telefon
    email?: number;           // E-Mail
    linkedin?: number;        // LinkedIn
    xing?: number;            // Xing
    events?: number;          // Events/Messen
    referrals?: number;       // Empfehlungen
    coldVisits?: number;      // Kaltbesuche
    contentMarketing?: number; // Content Marketing
    paidAds?: number;         // Bezahlte Werbung
    partner?: number;         // Partnerschaften
    other?: number;           // Sonstiges
    // Wizard-Felder (neuere Benennung)
    inbound?: number;
    outbound?: number;
    partners?: number;
    existing?: number;
    [key: string]: number | undefined;
  };
  
  // Conversion Rates (zwei separate Metriken)
  contactToDemoRate?: number; // Kontakte → Demos Conversion
  contactToDemoRateUnknown?: boolean;
  
  demoToSaleRate?: number; // Demos → Verkäufe Conversion
  demoToSaleRateUnknown?: boolean;
  
  monthlyContacts?: string;  // Dropdown value
  monthlyContactsExact?: number;  // Exact value
  monthlyContactsUnknown?: boolean; // "Nicht bekannt" Flag
  
  averageLeadResponseTime?: string;
  leadResponseTimeUnknown?: boolean; // "Nicht bekannt" Flag
  
  followUpRate?: number;
  followUpRateUnknown?: boolean; // "Nicht bekannt" Flag
  
  // Neue Funnel-Analyse Felder
  funnelData?: {
    monthlyRevenue?: number;
    monthlyRevenueUnknown?: boolean;
    monthlyContacts?: number;
    monthlyContactsUnknown?: boolean;
    monthlyDiscoveryCalls?: number;
    monthlyDiscoveryCallsUnknown?: boolean;
    monthlyPresentations?: number;
    monthlyPresentationsUnknown?: boolean;
    monthlyDeals?: number;
    monthlyDealsUnknown?: boolean;
    averageDealSize: number;
    averageDealSizeUnknown?: boolean;
    salesCycleMonths: number;
    salesCycleMonthsUnknown?: boolean;
    responseTime: 'under-5min' | 'under-1h' | 'same-day' | '1-3days' | 'over-3days';
    responseTimeUnknown?: boolean;
    followUpDiscipline: 'very-structured' | 'partly-structured' | 'irregular' | 'rarely';
    followUpDisciplineUnknown?: boolean;
    leadQuality: 'very-high' | 'good' | 'mixed' | 'weak';
    leadQualityUnknown?: boolean;
    pipelineTransparency: 'full' | 'partial' | 'low' | 'minimal';
    pipelineTransparencyUnknown?: boolean;
    conversionContactToDiscovery?: number;
    conversionDiscoveryToPresentation?: number;
    conversionPresentationToDeal?: number;
  };
  
  // Team-Performance-Daten
  teamPerformance?: {
    topPerformers: number;
    avgPerformers: number;
    lowPerformers: number;
    avgDealsPerMonth: number;
    avgConversionRate: number;
  };
}

export type AnswerType = 'scale' | 'multiple' | 'boolean' | 'text';

export interface AuditQuestion {
  id: string;
  categoryId: string;
  text: string;
  tooltip?: string;
  answerType: AnswerType;
  options?: string[];
  weight: number;
  maxScore: number;
}

export interface AuditCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  maxScore: number;
}

export interface CategoryScore {
  categoryId: string;
  score: number;
  maxScore: number;
  percentage: number;
}

export interface AuditResult {
  totalScore: number;
  maxScore: number;
  percentage: number;
  maturityLevel: string;
  categoryScores: CategoryScore[];
  completedAt: Date;
  // Extended fields present when stored from MasterResult
  views?: {
    hero?: {
      monthlyRevenueGap?: number;
      annualRevenueGap?: number;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  normalizedData?: {
    monthlyDeals?: number;
    averageDealSize?: number;
    teamSize?: number;
    [key: string]: unknown;
  };
  computedMetrics?: {
    convPresentationToDeal?: number;
    benchmarkPresentationToDeal?: number;
    [key: string]: unknown;
  };
  simulation?: {
    current?: { revenue?: number; [key: string]: unknown };
    scenarios?: {
      realistic?: {
        potential?: { deals?: number; revenue?: number; [key: string]: unknown };
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  topLevers?: unknown[];
}

export interface MaturityLevel {
  name: string;
  minScore: number;
  maxScore: number;
  color: string;
  description: string;
}

export const AUDIT_CATEGORIES: AuditCategory[] = [
  {
    id: 'strategie',
    name: 'Strategie',
    description: 'ICP, Positionierung und Zielmärkte',
    icon: 'Target',
    maxScore: 100,
  },
  {
    id: 'lead-generation',
    name: 'Lead Generation',
    description: 'Outbound, Marketing Funnel und Lead Quellen',
    icon: 'Users',
    maxScore: 100,
  },
  {
    id: 'sales-prozess',
    name: 'Sales Prozess',
    description: 'Phasen, Conversion Rates und Forecast',
    icon: 'TrendingUp',
    maxScore: 100,
  },
  {
    id: 'tools',
    name: 'Tools & Automatisierung',
    description: 'CRM, Datenqualität und Automationen',
    icon: 'Settings',
    maxScore: 100,
  },
  {
    id: 'organisation',
    name: 'Team & Organisation',
    description: 'Rollen, Training und Incentives',
    icon: 'Briefcase',
    maxScore: 100,
  },
];

export const MATURITY_LEVELS: MaturityLevel[] = [
  {
    name: 'Kritisch',
    minScore: 0,
    maxScore: 150,
    color: 'destructive',
    description: 'Ihr Vertrieb benötigt dringende strukturelle Verbesserungen',
  },
  {
    name: 'Im Aufbau',
    minScore: 150,
    maxScore: 300,
    color: 'warning',
    description: 'Grundlagen vorhanden, aber erhebliches Optimierungspotenzial',
  },
  {
    name: 'Strukturiert',
    minScore: 300,
    maxScore: 400,
    color: 'success',
    description: 'Solide Vertriebsstruktur mit klaren Prozessen',
  },
  {
    name: 'Skalierbar',
    minScore: 400,
    maxScore: 500,
    color: 'primary',
    description: 'Exzellente Vertriebsorganisation bereit für Wachstum',
  },
];

export function calculateCategoryScore(
  answers: Record<string, any>,
  questions: AuditQuestion[],
  categoryId: string
): CategoryScore {
  const categoryQuestions = questions.filter(q => q.categoryId === categoryId);
  const maxScore = categoryQuestions.reduce((sum, q) => sum + q.maxScore, 0);
  
  let score = 0;
  categoryQuestions.forEach(question => {
    const answer = answers[question.id];
    
    // Wenn keine Antwort vorhanden ist, zählt die Frage als 0 Punkte
    if (answer === undefined || answer === null) {
      // score += 0; (explizit: keine Punkte für unbeantwortete Fragen)
      return;
    }
    
    if (question.answerType === 'scale') {
      // Scale: 1-5, wobei 1 = 20% und 5 = 100% der maxScore
      score += (answer / 5) * question.maxScore;
    } else if (question.answerType === 'boolean') {
      // Boolean: true = volle Punkte, false = 0 Punkte
      score += answer ? question.maxScore : 0;
    } else if (question.answerType === 'multiple' && Array.isArray(answer)) {
      // Multiple: Anteil der ausgewählten Optionen
      const selectedCount = answer.length;
      const totalOptions = question.options?.length || 1;
      score += (selectedCount / totalOptions) * question.maxScore;
    }
  });
  
  return {
    categoryId,
    score: Math.round(score),
    maxScore,
    percentage: Math.round((score / maxScore) * 100),
  };
}

export function calculateTotalScore(
  categoryScores: CategoryScore[]
): { totalScore: number; maxScore: number; percentage: number } {
  const totalScore = categoryScores.reduce((sum, cs) => sum + cs.score, 0);
  const maxScore = categoryScores.reduce((sum, cs) => sum + cs.maxScore, 0);
  const percentage = Math.round((totalScore / maxScore) * 100);
  
  return { totalScore, maxScore, percentage };
}

export function getMaturityLevel(totalScore: number): MaturityLevel {
  return MATURITY_LEVELS.find(
    level => totalScore >= level.minScore && totalScore <= level.maxScore
  ) || MATURITY_LEVELS[0];
}

export function formatScore(score: number): string {
  return score.toFixed(1);
}

export function getProgressPercentage(current: number, total: number): number {
  return Math.round((current / total) * 100);
}